import Feature, { FeatureLike } from "ol/Feature";
import { LineString, Point, Polygon } from "ol/geom";
import {
  Circle as CircleStyle,
  Fill,
  RegularShape,
  Stroke,
  Style,
  Text,
} from "ol/style";

// @ts-ignore
import { DrawType, getStyleForDrawType } from "./tiffDrawHelpers.ts";
// @ts-ignore
import { formatMetersToString } from "../../../utils/stringUtils.js";
// @ts-ignore
import { isValidPolygon } from "../../../utils/geo.js";
import { Coordinate } from "ol/coordinate.js";

export const Colors = {
  hiddenColor: "rgba(0, 0, 0, 0)",
  normalStrokeColorLine: "rgba(112, 126, 198, 1)",
  normalStrokeColorPolygon: "rgba(122, 191, 97, 1)",
  normalStrokeColorPolygonRecord: "rgba(255, 255, 255, 1)",
  selectedStrokeColor: "rgba(255, 0, 0, 0.9)",
  normalFillColorLine: "rgba(0, 10, 220, 0.1)",
  normalFillColorPolygon: "rgba(0, 220, 10, 0.1)",
  normalFillColorPolygonRecord: "rgba(255, 255, 255, 0.1)",
  selectedFillColor: "rgba(255, 0, 0, 0.1)",
  normalPointColor: "rgba(245, 211, 0, 1)",
};

const createInitialStyle = (color: keyof typeof Colors) => {
  return new Style({
    fill: new Fill({
      color: Colors[color],
    }),
    stroke: new Stroke({
      color: Colors[color],
      width: 2,
    }),
    image: new CircleStyle({
      radius: 5,
      stroke: new Stroke({
        color: Colors[color],
      }),
      fill: new Fill({
        color: Colors[color],
      }),
    }),
  });
};

export const polygonStyle = createInitialStyle("normalStrokeColorPolygon");
export const lineStyle = createInitialStyle("normalStrokeColorLine");
export const pointStyle = createInitialStyle("normalPointColor");

export const getPointStyles = ({ feature }: { feature: FeatureLike }) => {
  const geometry = feature.getGeometry();
  const isLineString = geometry instanceof LineString;
  const isPolygon = geometry instanceof Polygon;

  let finalCoords: Coordinate[] = [];

  if (isLineString) {
    finalCoords = geometry.getCoordinates();
  }

  if (isPolygon) {
    const coords = geometry.getCoordinates();
    finalCoords = coords[0];
  }

  return finalCoords.flatMap((coord) => [
    new Style({
      geometry: new Point(coord),
      image: new CircleStyle({
        radius: 5,
        fill: new Fill({
          color: isLineString ? "blue" : "rgba(0, 220, 10, 0.9)",
        }),
        stroke: new Stroke({
          color: "white",
          width: 2,
        }),
      }),
    }),
  ]);
};

export const getLabelStyle = (selected: boolean) =>
  new Style({
    text: new Text({
      font: selected ? "18px Calibri,sans-serif" : "14px Calibri,sans-serif",
      fill: new Fill({
        color: "rgba(255, 255, 255, 1)",
      }),
      backgroundFill: new Fill({
        color: "rgba(0, 0, 0, 0.7)",
      }),
      padding: [3, 3, 3, 3],
      textBaseline: "bottom",
      offsetY: -15,
    }),
    image: new RegularShape({
      radius: 8,
      points: 3,
      angle: Math.PI,
      displacement: [0, 10],
      fill: new Fill({
        color: "rgba(0, 0, 0, 0.7)",
      }),
    }),
  });

const setStrokeColor = (color: keyof typeof Colors, styles: Style[]) => {
  styles[0].getStroke().setColor(Colors[color]);
};

const setFillColor = (color: keyof typeof Colors, styles: Style[]) => {
  styles[0].getFill().setColor(Colors[color]);
};

export const featureStyleFunction = ({
  feature,
  drawType,
  t,
}: {
  feature: Feature | FeatureLike;
  drawType?: DrawType;
  t: (translationKey: string) => string;
}) => {
  const styles = [getStyleForDrawType(drawType)];
  const geometry = feature.getGeometry();

  if (!geometry) return;

  const geometryType = geometry.getType();

  const hidden = feature.get("hidden") === true;
  const selected = feature.get("selected");
  const toBeStored = feature.get("toBeStored");
  const editable = feature.get("editable");
  const name = feature.get("name");
  const isRecord = feature.get("isRecord") === true;
  const showValue = feature.get("showValue") === true;

  const labelStyle = getLabelStyle(selected);

  if (hidden) {
    setStrokeColor("hiddenColor", styles);
    setFillColor("hiddenColor", styles);
  } else if (!drawType || geometryType === drawType) {
    if (geometryType === "LineString") {
      const lineGeom = geometry as LineString;
      const point = new Point(lineGeom.getLastCoordinate());

      if (!isRecord) {
        const label = formatMetersToString({
          length: lineGeom.getLength(),
          units: "m",
        });

        if (label) {
          labelStyle.setGeometry(point);
          labelStyle.getText().setText(label);
          styles.push(labelStyle);
        }
      }

      setStrokeColor("normalStrokeColorLine", styles);
      setFillColor("normalFillColorLine", styles);
    } else if (geometryType === "Polygon") {
      const polyGeom = geometry as Polygon;
      const point = new Point(polyGeom.getInteriorPoint().getCoordinates());

      if (!isRecord || showValue) {
        const label = isValidPolygon(polyGeom.getCoordinates())
          ? formatMetersToString({ length: polyGeom.getArea(), units: "m²" })
          : t("tiffViewer.unvalidPolygon");

        if (label) {
          labelStyle.setGeometry(point);
          labelStyle.getText().setText(label);
          styles.push(labelStyle);
        }
      }
      setStrokeColor(
        isRecord
          ? "normalStrokeColorPolygonRecord"
          : "normalStrokeColorPolygon",
        styles,
      );
      setFillColor(
        isRecord ? "normalFillColorPolygonRecord" : "normalFillColorPolygon",
        styles,
      );
    } else if (geometryType === "Point") {
      const pointGeom = geometry as Point;
      const point = new Point(pointGeom.getCoordinates());

      // change point color
      const image = styles[0].getImage();
      if (image instanceof CircleStyle) {
        image.setFill(new Fill({ color: "rgba(245, 211, 0, 1)" }));
        image.setStroke(new Stroke({ color: Colors.hiddenColor }));
      }

      const label = name;

      if (label) {
        labelStyle.setGeometry(point);
        labelStyle.getText().setText(label);
        styles.push(labelStyle);
      }
    }
  }

  if (hidden) {
    setStrokeColor("hiddenColor", styles);
    setFillColor("hiddenColor", styles);
    if (geometryType === "Point") {
      const image = styles[0].getImage();

      if (image instanceof CircleStyle) {
        image.setFill(new Fill({ color: "rgba(245, 211, 0, 1)" }));
        image.setStroke(new Stroke({ color: Colors.hiddenColor }));
      }
    }
  } else {
    if (selected) {
      setStrokeColor("selectedStrokeColor", styles);
      setFillColor("selectedFillColor", styles);
    }
  }

  if ((!toBeStored || editable) && !hidden) {
    // show circle at each vertex of the feature

    const pointStyles = getPointStyles({ feature });
    styles.push(...pointStyles);
  }

  return styles;
};
